import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Sparkles, ShieldCheck, Scissors, Layers, Activity, CheckCircle2,
  MapPin, Phone, MessageCircle, Clock, FileText, ScanLine, Star,
  Stethoscope, ArrowUpRight,
} from "lucide-react";

export const WHATSAPP = "+52 241 102 2092";
export const WHATSAPP_LINK = "https://wa.me/522411022092";
export const MAPS_LINK = "https://www.google.com/maps/place/UATx:+Facultad+de+Odontolog%C3%ADa/@19.3275975,-98.2301304,604m/data=!3m1!1e3!4m6!3m5!1s0x85cfd9428d896201:0xfaa34b4b17b0cde8!8m2!3d19.327656!4d-98.2290652!16s%2Fg%2F11bt_hqk3h";
const MAPS_EMBED = "https://maps.google.com/maps?q=19.327656,-98.2290652&z=17&output=embed";

export const SERVICES = [
  { icon: ShieldCheck, title: "Eliminación de caries", desc: "Remoción precisa y sellado para detener el avance del daño.", span: "lg:col-span-3" },
  { icon: Sparkles, title: "Limpiezas dentales", desc: "Eliminación de placa y sarro para una boca sana y fresca.", span: "lg:col-span-3" },
  { icon: Scissors, title: "Extracciones dentales", desc: "Procedimientos seguros y cuidadosos cuando son necesarios.", span: "lg:col-span-2" },
  { icon: Layers, title: "Evaluación para prótesis dental", desc: "Valoración para recuperar función y estética de tu sonrisa.", span: "lg:col-span-2" },
  { icon: Activity, title: "Endodoncias", desc: "En incisivos y premolares, tratando el conducto con detalle.", span: "lg:col-span-2" },
];

const INCLUDES = [
  { icon: Stethoscope, text: "Valoración inicial" },
  { icon: FileText, text: "Elaboración de expediente clínico" },
  { icon: ScanLine, text: "Radiografías si son necesarias para diagnóstico" },
];

const HERO_LINES = ["¿Buscas", "atención dental", "de confianza?"];

export const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const EASE = [0.22, 1, 0.36, 1];
const MASK_IN = { initial: { y: "110%" }, animate: { y: "0%" } };
const FADE_IN = { initial: { opacity: 0 }, animate: { opacity: 1 } };
const FADE_UP_IN = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const FADE_UP_SM = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };
const SCALE_IN = { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 } };
const HERO_DELAYS = { badge: { delay: 0.2 }, signature: { delay: 0.9 }, intro: { delay: 1.1 }, ctas: { delay: 1.25 }, portrait: { delay: 0.6, duration: 1 }, stars: { delay: 1.3 } };
export const REVEAL = { variants: fadeUp, initial: "hidden", whileInView: "show" };
const VIEW_30 = { once: true, amount: 0.3 };
const VIEW_20 = { once: true, amount: 0.2 };

function MaskLine({ children, delay = 0 }) {
  return (
    <span className="reveal-mask">
      <motion.span className="block" {...MASK_IN} transition={{ duration: 0.9, delay, ease: EASE }}>
        {children}
      </motion.span>
    </span>
  );
}

export function Chapter({ num, label }) {
  return (
    <div className="flex items-center gap-3 mb-8" data-testid={`chapter-${num}`}>
      <span className="font-display text-sm font-bold" style={{ color: "var(--mint-dark)" }}>{num}</span>
      <span className="h-px w-10" style={{ background: "var(--mint)" }} />
      <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: "var(--text-muted)" }}>{label}</span>
    </div>
  );
}

export function Nav() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md" style={{ background: "rgba(244,247,245,0.7)", borderBottom: "1px solid var(--border)" }}>
      <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
        <span className="font-display text-lg font-extrabold tracking-tight" style={{ color: "var(--text-dark)" }}>
          Rebeca García<span style={{ color: "var(--mint-dark)" }}>.</span>
        </span>
        <a href="#agendar" data-testid="nav-cta"
          className="hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
          style={{ background: "var(--mint-dark)" }}>
          Agendar mi cita <ArrowUpRight size={16} />
        </a>
      </div>
    </nav>
  );
}

export function Hero() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={heroRef} className="relative mx-auto max-w-7xl px-6 pt-32 pb-16 lg:pt-44 lg:pb-24">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div {...FADE_IN} transition={HERO_DELAYS.badge}
            className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em]"
            style={{ background: "var(--mint-light)", color: "var(--mint-dark)" }}>
            <Sparkles size={14} /> Estudiante de Odontología
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter leading-[0.9] text-5xl sm:text-6xl lg:text-7xl" style={{ color: "var(--text-dark)" }}>
            {HERO_LINES.map((line, i) => (
              <MaskLine key={line} delay={0.35 + i * 0.12}>{line}</MaskLine>
            ))}
          </h1>

          <motion.p {...FADE_UP_IN} transition={HERO_DELAYS.signature}
            className="mt-6 font-signature text-4xl sm:text-5xl -rotate-2 inline-block" style={{ color: "var(--mint-dark)" }}>
            Hola, soy Rebeca García
          </motion.p>

          <motion.p {...FADE_IN} transition={HERO_DELAYS.intro}
            className="mt-6 max-w-md text-base sm:text-lg leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Realizo tratamientos en la Facultad de Odontología de la UATx ubicada en
            Tlaxcala Centro.
          </motion.p>

          <motion.div {...FADE_IN} transition={HERO_DELAYS.ctas} className="mt-9 flex flex-wrap items-center gap-4">
            <a href="#agendar" data-testid="hero-cta"
              className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-transform hover:scale-[1.03]"
              style={{ background: "var(--mint-dark)" }}>
              Agendar mi cita
              <span className="transition-transform group-hover:translate-x-1"><ArrowUpRight size={18} /></span>
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border px-6 py-4 text-base font-semibold transition-colors hover:bg-white"
              style={{ borderColor: "var(--border)", color: "var(--text-dark)" }}>
              <MessageCircle size={18} /> WhatsApp
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div style={{ y: portraitY }} {...SCALE_IN} transition={HERO_DELAYS.portrait} className="relative mx-auto max-w-sm">
            <div className="relative overflow-hidden rounded-[999px] border-4 border-white shadow-2xl aspect-[3/4]" data-testid="hero-portrait"
              style={{ background: "var(--mint-light)" }}>
              <img
                src="https://customer-assets-lxgj4vgw.emergentagent.net/job_dental-rebeca-mx/artifacts/r4obh2sb_hf_20260818_041314_3667b082-2bb5-409f-9a38-37f9b69e9f3f.webp"
                alt="Rebeca García, estudiante de odontología con bata blanca"
                className="h-full w-full object-cover"
              />
            </div>
            <motion.div {...FADE_UP_SM} transition={HERO_DELAYS.stars}
              className="absolute -bottom-4 -left-4 rounded-2xl bg-white px-5 py-3 shadow-xl flex items-center gap-3">
              <div className="flex -space-x-1 text-[var(--mint-dark)]">
                {["s1", "s2", "s3", "s4", "s5"].map((k) => <Star key={k} size={14} fill="currentColor" />)}
              </div>
              <span className="text-xs font-semibold" style={{ color: "var(--text-dark)" }}>Trato humano y cercano</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function MarqueeStrip() {
  return (
    <div className="py-4 border-y" style={{ background: "var(--mint-light)", borderColor: "var(--border)" }}>
      <Marquee speed={30} gradient={false}>
        {["Atención especializada", "Materiales premium", "Trato humano", "Tlaxcala Centro", "Sonrisas sanas"].map((t) => (
          <span key={t} className="mx-8 inline-flex items-center gap-8 font-display text-xl font-bold" style={{ color: "var(--mint-dark)" }}>
            {t} <Sparkles size={18} />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export function IncludesSection() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:py-32">
      <motion.div {...REVEAL} viewport={VIEW_30}>
        <Chapter num="01" label="Incluye — Primera consulta" />
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-display font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-5xl" style={{ color: "var(--text-dark)" }}>
              Todo lo esencial para empezar tu tratamiento
            </h2>
            <ul className="mt-10 space-y-5">
              {INCLUDES.map((it, i) => (
                <li key={it.text} className="flex items-start gap-4" data-testid={`include-${i}`}>
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white" style={{ background: "var(--mint-dark)" }}>
                    <it.icon size={18} />
                  </span>
                  <span className="text-lg font-medium pt-1.5" style={{ color: "var(--text-dark)" }}>{it.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-[2rem] p-8 sm:p-10 text-white relative overflow-hidden" style={{ background: "var(--mint-dark)" }} data-testid="cost-card">
              <span className="text-xs font-semibold uppercase tracking-[0.25em] opacity-80">Costo de primera consulta</span>
              <div className="mt-3 font-display font-extrabold tracking-tighter leading-none text-6xl sm:text-7xl">
                $200
              </div>
              <span className="font-display text-2xl font-bold opacity-90">MXN</span>
              <div className="mt-8 flex items-start gap-3 rounded-2xl bg-white/10 p-4">
                <Clock size={18} className="mt-0.5 shrink-0" />
                <p className="text-sm leading-relaxed">
                  Se requiere disponibilidad de horario para asistir al tratamiento.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

export function ServicesSection({ selected, onSelect }) {
  return (
    <section className="px-6 py-24 lg:py-32" style={{ background: "var(--text-dark)" }}>
      <div className="mx-auto max-w-7xl">
        <motion.div {...REVEAL} viewport={VIEW_30}>
          <div className="flex items-center gap-3 mb-8">
            <span className="font-display text-sm font-bold text-[var(--mint)]">02</span>
            <span className="h-px w-10 bg-[var(--mint)]" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">Servicios disponibles</span>
          </div>
          <h2 className="font-display font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-5xl text-white max-w-2xl">
            Tratamientos pensados en tu bienestar
          </h2>
          <p className="mt-4 text-sm text-white/50" data-testid="services-hint">Elige el tratamiento que te interesa y te llevamos al formulario.</p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-6 gap-4">
          {SERVICES.map((s, i) => {
            const active = selected === s.title;
            return (
              <motion.button type="button" key={s.title} {...REVEAL} viewport={VIEW_20}
                transition={{ delay: i * 0.05 }} onClick={() => onSelect(s.title)} aria-pressed={active}
                className={`group relative text-left rounded-[1.75rem] border p-8 transition-colors ${s.span} ${active ? "border-[var(--mint)] bg-[var(--mint-light)]" : "border-white/10 bg-white/[0.03] hover:bg-[var(--mint-light)]"}`}
                data-testid={`service-card-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--mint)] text-[var(--text-dark)]">
                    <s.icon size={22} />
                  </span>
                  <CheckCircle2 size={20} className={`transition-colors ${active ? "text-[var(--mint-dark)]" : "text-white/30 group-hover:text-[var(--mint-dark)]"}`} />
                </div>
                <h3 className={`mt-6 font-display text-2xl font-bold transition-colors ${active ? "text-[var(--text-dark)]" : "text-white group-hover:text-[var(--text-dark)]"}`}>{s.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed transition-colors ${active ? "text-[var(--mint-dark)]" : "text-white/50 group-hover:text-[var(--mint-dark)]"}`}>{s.desc}</p>
                {active && (
                  <span className="mt-4 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: "var(--mint-dark)" }} data-testid={`service-selected-${i}`}>
                    <CheckCircle2 size={12} /> Seleccionado
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="px-6 pt-20 pb-10" style={{ background: "var(--mint-dark)" }}>
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <div>
            <p className="font-signature text-3xl text-[var(--mint-light)]">Tu salud bucal</p>
            <h2 className="font-display font-extrabold tracking-tighter leading-[0.9] text-5xl sm:text-6xl lg:text-7xl text-white max-w-4xl">
              en buenas manos
            </h2>
          </div>
          <a href="https://uatx.mx" target="_blank" rel="noopener noreferrer" className="shrink-0 opacity-90 transition-opacity hover:opacity-100" data-testid="footer-uatx-logo">
            <img
              src="https://customer-assets-lxgj4vgw.emergentagent.net/job_dental-rebeca-mx/artifacts/5ylsmd0y_logo%20uatxi%202019.png"
              alt="Universidad Autónoma de Tlaxcala"
              className="h-20 sm:h-24 w-auto"
              loading="lazy"
            />
          </a>
        </div>

        <div className="mt-16 grid sm:grid-cols-3 gap-8 border-t border-white/20 pt-10">
          <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 group" data-testid="footer-location">
            <MapPin size={20} className="mt-1 shrink-0 text-[var(--mint-light)]" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/60">Ubicación</p>
              <p className="mt-1 text-white group-hover:underline">UATx: Facultad de Odontología, Rivereña S/N, Centro, Tlaxcala.</p>
              <p className="mt-1 text-xs text-[var(--mint-light)]">Ver en Google Maps →</p>
            </div>
          </a>
          <a href={`tel:${WHATSAPP.replace(/\s/g, "")}`} className="flex items-start gap-3 group" data-testid="footer-phone">
            <Phone size={20} className="mt-1 shrink-0 text-[var(--mint-light)]" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/60">Teléfono</p>
              <p className="mt-1 text-white group-hover:underline">{WHATSAPP}</p>
            </div>
          </a>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 group" data-testid="footer-whatsapp">
            <MessageCircle size={20} className="mt-1 shrink-0 text-[var(--mint-light)]" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/60">WhatsApp</p>
              <p className="mt-1 text-white group-hover:underline">{WHATSAPP}</p>
            </div>
          </a>
        </div>

        <div className="mt-10 overflow-hidden rounded-[1.75rem] border border-white/20" data-testid="footer-map">
          <iframe
            src={MAPS_EMBED}
            title="Mapa: UATx Facultad de Odontología"
            className="w-full h-72 block"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/20 pt-6">
          <p className="text-sm text-white/60">© {new Date().getFullYear()} Rebeca García — Odontología</p>
          <div className="flex items-center gap-6">
            <a href="/aviso-legal" className="text-sm text-white/60 hover:text-white" data-testid="footer-legal-link">Aviso Legal</a>
            <a href="/aviso-de-privacidad" className="text-sm text-white/60 hover:text-white" data-testid="footer-privacy-link">Aviso de Privacidad</a>
            <a href="/admin" className="text-sm text-white/60 hover:text-white" data-testid="footer-admin-link">Panel de citas</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
